import React, { useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useParams } from 'react-router-dom';
import { CircularProgress, IconButton } from '@material-ui/core';
import ArrowBackIcon from '@material-ui/icons/ArrowBack';
import TrendingUpIcon from '@material-ui/icons/TrendingUp';
import WarningIcon from '@material-ui/icons/Warning';
import EmojiObjectsIcon from '@material-ui/icons/EmojiObjects';
import TimelineIcon from '@material-ui/icons/Timeline';
import SecurityIcon from '@material-ui/icons/Security';
import AssignmentTurnedInIcon from '@material-ui/icons/AssignmentTurnedIn';

import { ApplicationState } from '../../../store';
import { selectInsight } from '../../../store/modules/insights/actions';
import history from '../../../services/history';
import api from '../../../services/api';
import { useStyles } from './InsightsStyles';

function colorizeValue(text: string) {
  // Color R$ values green/red. Only match R$ followed by optional minus and digits.
  var parts = text.split(/(-?R\$\s*-?[\d]+(?:[.,][\d]+)*)/g);
  return parts.map(function(part, i) {
    if (i % 2 === 1) {
      var isNegative = part.indexOf('-') !== -1;
      var color = isNegative ? '#FF6347' : '#4BDC4B';
      return React.createElement('span', { key: 'c' + i, style: { color: color, fontWeight: 600 } }, part);
    }
    return part;
  });
}

function renderFormattedText(text: string) {
  if (!text) return null;
  // First process bold markers
  var boldParts = text.split(/\*\*(.*?)\*\*/g);
  return boldParts.map(function(part, i) {
    if (i % 2 === 1) {
      // Bold part — also colorize values inside
      return React.createElement('strong', { key: i, style: { color: '#ffffffee', fontWeight: 700 } }, colorizeValue(part));
    }
    // Normal part — colorize values
    return React.createElement(React.Fragment, { key: i }, colorizeValue(part));
  });
}

const sectionConfig = [
  { key: 'strengths', title: 'Pontos Fortes', icon: TrendingUpIcon, color: '#4BDC4B' },
  { key: 'weaknesses', title: 'Pontos de Melhoria', icon: WarningIcon, color: '#FF6347' },
  { key: 'strategies', title: 'Estratégias', icon: EmojiObjectsIcon, color: '#ffee58' },
  { key: 'patterns', title: 'Padrões Identificados', icon: TimelineIcon, color: '#5C6BC0' },
  { key: 'riskManagement', title: 'Gestão de Risco', icon: SecurityIcon, color: '#FF9800' },
] as const;

export default function InsightDetailView() {
  const classes = useStyles();
  const dispatch = useDispatch();
  const { id } = useParams<{ id: string }>();
  const { currentInsight } = useSelector(
    (state: ApplicationState) => state.insights
  );

  useEffect(() => {
    if (!currentInsight || currentInsight._id !== id) {
      // Fetch from API if not in store
      api.get('/insights/' + id).then(function(response) {
        dispatch(selectInsight(response.data));
      }).catch(function() {
        history.push('/insights');
      });
    }
  }, [id]); // eslint-disable-line

  function formatShortDate(dateStr: string) {
    if (!dateStr) return '—';
    var parts = dateStr.split('-');
    return parts[2] + '/' + parts[1] + '/' + parts[0];
  }

  if (!currentInsight || currentInsight._id !== id) {
    return (
      <div className={classes.container}>
        <div style={{ textAlign: 'center', padding: '60px 0' }}>
          <CircularProgress style={{ color: '#ffee58' }} size={36} />
        </div>
      </div>
    );
  }

  return (
    <div className={classes.container}>
      {/* Back button + Title centered */}
      <div className={classes.detailHeader}>
        <IconButton
          onClick={function() { history.push('/insights'); }}
          style={{ color: '#ffffffcc', padding: '8px' }}
        >
          <ArrowBackIcon />
        </IconButton>
        <div className={classes.detailHeaderCenter}>
          <div className={classes.detailTitle}>Insight</div>
          {currentInsight.periodStart && (
            <div className={classes.detailDate}>
              {formatShortDate(currentInsight.periodStart)} — {formatShortDate(currentInsight.periodEnd)}
            </div>
          )}
        </div>
        {/* Spacer to balance the back button */}
        <div style={{ width: '40px' }} />
      </div>

      {/* Score | Stats | Resultado */}
      <div className={classes.summaryLayout}>
        {currentInsight.score > 0 && (
          <div className={classes.scoreItem}>
            <div className={classes.scoreCircle} style={{
              borderColor: currentInsight.score >= 70 ? '#4BDC4B' : currentInsight.score >= 40 ? '#ffee58' : '#FF6347'
            }}>
              <span className={classes.scoreNumber} style={{
                color: currentInsight.score >= 70 ? '#4BDC4B' : currentInsight.score >= 40 ? '#ffee58' : '#FF6347'
              }}>
                {currentInsight.score}
              </span>
            </div>
            <div className={classes.scoreLabel}>Nota</div>
            {currentInsight.scoreExplanation && (
              <div className={classes.scoreExplanation}>{currentInsight.scoreExplanation}</div>
            )}
          </div>
        )}

        <div className={classes.statsCenter}>
          <div className={classes.summaryItem}>
            <div className={classes.summaryLabel}>Trades</div>
            <div className={classes.summaryValue}>
              {currentInsight.tradeSummary.totalTrades}
            </div>
          </div>
          <div className={classes.summaryItem}>
            <div className={classes.summaryLabel}>Win Rate</div>
            <div className={classes.summaryValue} style={{ color: '#4BDC4B' }}>
              {currentInsight.tradeSummary.winRate}%
            </div>
          </div>
          <div className={classes.summaryItem}>
            <div className={classes.summaryLabel}>Vitórias</div>
            <div className={classes.summaryValue} style={{ color: '#4BDC4B' }}>
              {currentInsight.tradeSummary.totalWins}
            </div>
          </div>
          <div className={classes.summaryItem}>
            <div className={classes.summaryLabel}>Derrotas</div>
            <div className={classes.summaryValue} style={{ color: '#FF6347' }}>
              {currentInsight.tradeSummary.totalLosses}
            </div>
          </div>
        </div>

        <div className={classes.resultadoItem}>
          <div className={classes.summaryLabel}>Resultado</div>
          <div
            className={classes.resultadoValue}
            style={{ color: currentInsight.tradeSummary.totalPnL >= 0 ? '#4BDC4B' : '#FF6347' }}
          >
            R$ {currentInsight.tradeSummary.totalPnL.toFixed(2)}
          </div>
        </div>
      </div>

      {/* Pontos Fortes — full width */}
      <div className={classes.insightCardFull}>
        <div className={classes.insightCardHeader}>
          <TrendingUpIcon style={{ color: '#4BDC4B', fontSize: 15 }} />
          <div className={classes.insightCardTitle}>Pontos Fortes</div>
        </div>
        <div className={classes.insightCardBody}>
          <div className={classes.insightCardText}>
            {renderFormattedText(currentInsight.sections.strengths)}
          </div>
        </div>
      </div>

      {/* Other Insight Cards — 2 columns */}
      <div className={classes.cardsGrid}>
        {sectionConfig.filter(function(s) { return s.key !== 'strengths'; }).map(function({ key, title, icon: Icon, color }) {
          return (
            <div key={key} className={classes.insightCard}>
              <div className={classes.insightCardHeader}>
                <Icon style={{ color: color, fontSize: 15 }} />
                <div className={classes.insightCardTitle}>{title}</div>
              </div>
              <div className={classes.insightCardBody}>
                <div className={classes.insightCardText}>
                  {renderFormattedText(currentInsight.sections[key])}
                </div>
              </div>
            </div>
          );
        })}
      </div>

      {/* Action Plan */}
      {currentInsight.sections.actionPlan && (
        <div className={classes.actionPlanCard}>
          <div className={classes.insightCardHeader}>
            <AssignmentTurnedInIcon style={{ color: '#4BDC4B', fontSize: 15 }} />
            <div className={classes.insightCardTitle}>Plano de Ação</div>
          </div>
          <div className={classes.actionPlanBody}>
            {currentInsight.sections.actionPlan.split('\n').map(function(line, i) {
              var trimmed = line.replace(/^[•-]\s*/, '').trim();
              if (!trimmed) return null;
              return (
                <div key={i} className={classes.actionPlanItem}>
                  <div className={classes.actionPlanBullet} />
                  <div className={classes.actionPlanText}>{renderFormattedText(trimmed)}</div>
                </div>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}
